import type { RoundSnapshot, RoundEvent, RoundAction } from "../contracts/RoundSnapshot";
import { validateFeatureFlowOrThrow } from "./FeatureFlowValidator";
import { ProtocolError } from "./ProtocolError";

/**
 * Same pattern: error includes a path so you can pinpoint the exact issue.
 */
export class RoundSnapshotValidationError extends ProtocolError {
  public readonly path: string;

  public constructor(path: string, message: string) {
    super(`RoundSnapshot validation failed at "${path}": ${message}`);
    this.name = "RoundSnapshotValidationError";
    this.path = path;
  }
}

/**
 * Validates the structural requirements the replay engine relies on, the round's total win on its last event
 * (decision 26), and the gamble offer the shell and the game read.
 * This is NOT game rules validation (no mask length checks, etc.).
 */
export function validateRoundSnapshotOrThrow(roundSnapshot: RoundSnapshot): void {
  if (!roundSnapshot) {
    throw new RoundSnapshotValidationError("roundSnapshot", "Value is null/undefined.");
  }

  if (typeof roundSnapshot.roundIdentifier !== "string" || roundSnapshot.roundIdentifier.length === 0) {
    throw new RoundSnapshotValidationError("roundIdentifier", "Must be a non-empty string.");
  }

  if (typeof roundSnapshot.roundStatus !== "string") {
    throw new RoundSnapshotValidationError("roundStatus", "Must be a string.");
  }

  if (!Array.isArray(roundSnapshot.possibleClientActions)) {
    throw new RoundSnapshotValidationError("possibleClientActions", "Must be an array.");
  }

  if (!Array.isArray(roundSnapshot.orderedEvents)) {
    throw new RoundSnapshotValidationError("orderedEvents", "Must be an array.");
  }

  // Every event's shape first: the feature-flow check reads event names and content, and until
  // 2026-09-26 it ran first, so a null or nameless event crashed it with a TypeError the shell showed
  // as "Something went wrong" instead of the protocol error it is (review finding 26).
  roundSnapshot.orderedEvents.forEach((event, eventIndex) => {
    validateEventOrThrow(event, eventIndex, eventIndex === roundSnapshot.orderedEvents.length - 1);
  });

  validateFeatureFlowOrThrow(roundSnapshot);

  validateGambleOfferOrThrow(roundSnapshot.gambleOfferDetails);
}

function validateEventOrThrow(event: RoundEvent, eventIndex: number, isLastEvent: boolean): void {
  const eventPath = `orderedEvents[${eventIndex}]`;

  if (!event || typeof event !== "object") {
    throw new RoundSnapshotValidationError(eventPath, "Event must be an object.");
  }

  if (typeof event.eventTypeName !== "string" || event.eventTypeName.length === 0) {
    throw new RoundSnapshotValidationError(`${eventPath}.eventTypeName`, "Must be a non-empty string.");
  }

  if (!event.eventContent || typeof event.eventContent !== "object") {
    throw new RoundSnapshotValidationError(`${eventPath}.eventContent`, "Missing eventContent object.");
  }

  // The two money fields are coerced with BigInt() downstream (the engine's settlement
  // precondition, the win extraction, the feature-finished plugin). A decimal, grouped or
  // exponent-formatted string would throw a bare SyntaxError from outside any validator, so the
  // shape is refused here as a ProtocolError with a path instead.
  validateOptionalMinorAmount(event.betAmountAtEvent, `${eventPath}.betAmountAtEvent`);
  validateOptionalMinorAmount(event.accumulatedWinAmountAfterEvent, `${eventPath}.accumulatedWinAmountAfterEvent`);
  if (event.betLinesAtEvent !== undefined && event.betLinesAtEvent !== null && !(Number.isInteger(event.betLinesAtEvent) && event.betLinesAtEvent >= 1)) {
    throw new RoundSnapshotValidationError(`${eventPath}.betLinesAtEvent`, `Must be a whole number of at least 1 if provided, got ${JSON.stringify(event.betLinesAtEvent)}.`);
  }

  const actions = event.eventContent.orderedActions;
  if (actions !== undefined) {
    if (!Array.isArray(actions)) {
      throw new RoundSnapshotValidationError(`${eventPath}.eventContent.orderedActions`, "Must be an array if provided.");
    }
    actions.forEach((action, actionIndex) => {
      validateActionOrThrow(action, eventIndex, actionIndex);
    });
  }

  // The last event's accumulated win is the round's total and the one figure the settlement is decided
  // on (decision 26). Until review 2026-09-27 #35 it was optional, and a winning round without it was
  // shown with its win and never settled. Other events may omit it.
  if (isLastEvent && (event.accumulatedWinAmountAfterEvent === undefined || event.accumulatedWinAmountAfterEvent === null)) {
    throw new RoundSnapshotValidationError(`${eventPath}.accumulatedWinAmountAfterEvent`, "Required on the round's last event: it is the round's total win, which decides the settlement.");
  }
}

/** A monetary field is a non-negative integer string in minor units, or absent. */
function validateOptionalMinorAmount(value: unknown, path: string): void {
  if (value === undefined || value === null) return;
  if (typeof value !== "string" || !/^\d+$/.test(value)) {
    throw new RoundSnapshotValidationError(path, `Must be a non-negative integer string in minor units, got ${JSON.stringify(value)}.`);
  }
}

/**
 * The gamble offer, when the reply carries one; absent or null is no offer (the shell logs a skip,
 * decision 23). The shell reads `options.length` and the game multiplies the win by an option's
 * multiplier with BigInt(), so a multiplier is a whole number of at least 1 — the backend's own rule
 * (decision 24). Until review 2026-09-27 #12 a malformed offer threw a TypeError or SyntaxError after
 * the round had settled and ended in the generic reload.
 */
function validateGambleOfferOrThrow(offer: RoundSnapshot["gambleOfferDetails"]): void {
  if (offer === undefined || offer === null) return;
  if (typeof offer !== "object" || Array.isArray(offer)) {
    throw new RoundSnapshotValidationError("gambleOfferDetails", "Must be an object if provided.");
  }

  const currentWinAmount: unknown = offer.currentWinAmountMinor;
  if (typeof currentWinAmount !== "string" || !/^\d+$/.test(currentWinAmount)) {
    throw new RoundSnapshotValidationError("gambleOfferDetails.currentWinAmountMinor", `Must be a non-negative integer string in minor units, got ${JSON.stringify(currentWinAmount)}.`);
  }
  validateOptionalMinorAmount(offer.gambleCeilingMinor, "gambleOfferDetails.gambleCeilingMinor");

  if (!Array.isArray(offer.options) || offer.options.length === 0) {
    throw new RoundSnapshotValidationError("gambleOfferDetails.options", "Must be a non-empty array.");
  }
  offer.options.forEach((option, optionIndex) => {
    const optionPath = `gambleOfferDetails.options[${optionIndex}]`;
    if (!option || typeof option !== "object") {
      throw new RoundSnapshotValidationError(optionPath, "Option must be an object.");
    }
    if (typeof option.optionId !== "string" || option.optionId.length === 0) {
      throw new RoundSnapshotValidationError(`${optionPath}.optionId`, "Must be a non-empty string.");
    }
    if (typeof option.multiplier !== "string" || !/^[1-9]\d*$/.test(option.multiplier)) {
      throw new RoundSnapshotValidationError(`${optionPath}.multiplier`, `Must be a whole number of at least 1, as a string, got ${JSON.stringify(option.multiplier)}.`);
    }
  });
}

function validateActionOrThrow(action: RoundAction, eventIndex: number, actionIndex: number): void {
  const actionPath = `orderedEvents[${eventIndex}].eventContent.orderedActions[${actionIndex}]`;

  if (!action || typeof action !== "object") {
    throw new RoundSnapshotValidationError(actionPath, "Action must be an object.");
  }

  if (typeof action.actionTypeName !== "string" || action.actionTypeName.length === 0) {
    throw new RoundSnapshotValidationError(`${actionPath}.actionTypeName`, "Must be a non-empty string.");
  }

  if (!action.actionPayload || typeof action.actionPayload !== "object") {
    throw new RoundSnapshotValidationError(`${actionPath}.actionPayload`, "Must be an object.");
  }

  if (action.actionPhaseName !== undefined) {
    if (!isValidActionPhaseName(action.actionPhaseName)) {
      throw new RoundSnapshotValidationError(`${actionPath}.actionPhaseName`, `Must be a valid ActionPhaseName. Got: ${String(action.actionPhaseName)}`);
    }
  }
}

function isValidActionPhaseName(value: unknown): boolean {
  return (
    value === "frame_prepare" ||
    value === "pre_removal" ||
    value === "win" ||
    value === "removal" ||
    value === "post_removal" ||
    value === "settlement" ||
    value === "other"
  );
}